'use strict';

const constants = require('../util/constants');
const addressValidation = require('../util/addressValidation');
const { writeFieldValue } = require('../util/addressForm');

const CHECKBOX_SELECTOR = '.js-use-account-data';
const INIT_MARKER = 'useAccountDataInitialized';
const EMPTY_RECIPIENT = { firstName: '', lastName: '', phone: '' };

/**
 * Normalizes a profile phone to the national number accepted by the phone
 * rule. A value that does not match after normalization is returned empty,
 * so it is never shortened into a valid-looking number.
 *
 * @param {string} phone - Phone as stored in the customer profile.
 * @returns {string} National digits, or an empty string.
 */
function normalizeProfilePhone(phone) {
    const digits = addressValidation.digitsOnly(phone);

    return constants.ADDRESS_FIELD_CONTRACT.phone.pattern.test(digits) ? digits : '';
}

/**
 * Reads the profile data rendered on the checkbox.
 *
 * @param {jQuery} $checkbox - The use-account-data checkbox.
 * @returns {Object} firstName, lastName and normalized phone.
 */
function getProfileData($checkbox) {
    return {
        firstName: $checkbox.attr('data-profile-first-name') || '',
        lastName: $checkbox.attr('data-profile-last-name') || '',
        phone: normalizeProfilePhone($checkbox.attr('data-profile-phone'))
    };
}

/**
 * Resolves the recipient inputs of the form. The name-suffix selectors match
 * the My Account, sidebar and checkout forms alike. The visible phone input is
 * renamed to '{name}Display' on window load, so both names are resolved and
 * the hidden payload input is left out.
 *
 * @param {jQuery} $form - The address form.
 * @returns {Object} jQuery inputs for firstName, lastName and phone.
 */
function getRecipientFields($form) {
    const selectors = constants.CHECKOUT_FIELD_SELECTORS;
    const phoneSelector = selectors.phone + ', ' + selectors.phone.replace(/Display(?=])/, '');

    return {
        firstName: $form.find(selectors.firstName),
        lastName: $form.find(selectors.lastName),
        phone: $form.find(phoneSelector).not('[type="hidden"]').first()
    };
}

/**
 * Writes the recipient fields.
 *
 * @param {jQuery} $form - The address form.
 * @param {Object} recipient - firstName, lastName and phone to write.
 */
function writeRecipientFields($form, recipient) {
    const fields = getRecipientFields($form);

    writeFieldValue(fields.firstName, recipient.firstName);
    writeFieldValue(fields.lastName, recipient.lastName);
    writeFieldValue(fields.phone, recipient.phone);
}

/**
 * Loads the profile data when the checkbox is checked and empties the
 * recipient fields when it is unchecked.
 */
function handleUseAccountDataChange() {
    const $checkbox = $(this);
    const recipient = $checkbox.is(':checked') ? getProfileData($checkbox) : EMPTY_RECIPIENT;

    writeRecipientFields($checkbox.closest('form'), recipient);
}

/**
 * Binds the checkbox change listener once for the whole document, which
 * also covers forms injected later by AJAX.
 */
function initEvents() {
    $(document)
        .off('change.useAccountData')
        .on('change.useAccountData', CHECKBOX_SELECTOR, handleUseAccountDataChange);
}

/**
 * Applies the rendered checkbox state of a My Account or sidebar form once: a
 * checked checkbox loads the profile data and an unchecked one keeps the
 * rendered values.
 *
 * @param {jQuery} $form - The address form.
 */
function initForm($form) {
    const $checkbox = $form.find(CHECKBOX_SELECTOR);

    if (!$checkbox.length || $form.data(INIT_MARKER)) {
        return;
    }

    $form.data(INIT_MARKER, true);
    initEvents();

    if ($checkbox.is(':checked')) {
        writeRecipientFields($form, getProfileData($checkbox));
    }
}

/**
 * Syncs the checkbox of a checkout form with its address selection: a new
 * address checks it and loads the profile data, a saved address unchecks it
 * and keeps the values of that address. The state is set without a change
 * event.
 *
 * @param {jQuery} $form - The address form.
 * @param {boolean} isNewAddress - Whether the selection is a new address.
 */
function applyAddressSelection($form, isNewAddress) {
    const $checkbox = $form.find(CHECKBOX_SELECTOR);

    if (!$checkbox.length) {
        return;
    }

    $checkbox.prop('checked', isNewAddress);

    if (isNewAddress) {
        writeRecipientFields($form, getProfileData($checkbox));
    }
}

module.exports = {
    normalizeProfilePhone: normalizeProfilePhone,
    getProfileData: getProfileData,
    initEvents: initEvents,
    initForm: initForm,
    applyAddressSelection: applyAddressSelection
};
